/*
  nb-layout.js — the ONE reducer for the homepage "layout" selection.

  First-principles: there is exactly ONE piece of state — the selected layout id
  ('' = default homepage; else HOME/WOMEN/MEN/GIFTING). Everything else (the active tab in BOTH
  menus, the drawer sub-menu panel, the swiper slide, the homepage #main content, the ?page_view
  URL, and persistence) is a pure function of that one id. This file owns the state, the chrome,
  and persistence, and it is the single entry point every input calls.

  Ownership split:
    - NBLayout (this file) = reducer. State + chrome (by IDENTITY, never DOM position) + persistence.
      It also DRIVES the content renderer: on the homepage, set() calls window.layoutEngineGoTo().
    - layout-engine.js  = content/URL renderer. Pure: swaps #main + pushes ?page_view. It never
      touches chrome or persistence, so there is no feedback loop.

  Data flow (one direction): every input -> NBLayout.set(id, opts):
    1. write data-nb-layout
    2. renderChrome(id)  — paint header tab + drawer tab + sub-menu panel + move swiper (by identity,
       with the swiper move SUPPRESSED so it can't re-fire realIndexChange)
    3. persist(id)                                   (skip with opts.persist:false)
    4. window.layoutEngineGoTo(id, push)             (skip with opts.content:false; homepage only)

  Persistence (cross-session, cross-page): the nb_layout COOKIE (id) is the sole durable source of
  truth (survives navigation + checkout on the storefront domain). It is mirrored to the cart
  ACTIVE_TAB attribute (id, debounced) for server continuity.

  Public API (window.NBLayout):
    current()          -> active layout id ('' = default)
    readPersisted()    -> layout id from the nb_layout cookie ('' = default)
    set(id, opts)      -> the reducer (opts: {persist, content, push} — all default true)
    renderChrome(id)   -> repaint chrome + reposition swiper to id (by identity)
    registerSwiper(sw) -> hand the drawer sub-menu Swiper instance to the reducer
    swiperSuppressed() -> true while WE are moving the swiper (so its handlers don't loop)
*/
(() => {
  const root = document.documentElement;
  const norm = (s) => (s == null ? '' : String(s)).trim().toUpperCase();

  let swiper = null;
  let suppressSwiper = false;

  /* ---------------- persistence ---------------- */
  const cookieGet = (name) => {
    const m = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  };
  const cookieSet = (name, v) => {
    document.cookie = name + '=' + encodeURIComponent(v) + ';path=/;max-age=31536000';
  };

  // The nb_layout COOKIE is the SOLE source of truth: written synchronously on every set() (always
  // current across a navigation), and cleared to '' by the logo. No cart ACTIVE_TAB fallback — an
  // empty cookie must mean DEFAULT; falling through to the stale/async ACTIVE_TAB made the logo
  // "restore" the previous layout instead of going to the default homepage.
  const readPersisted = () => {
    const raw = cookieGet('nb_layout');
    if (!raw || raw === 'undefined') return '';
    return norm(String(raw).split('_')[0]);
  };

  let cartTimer = null;
  const persist = (id) => {
    cookieSet('nb_layout', id);
    if (window.simply && simply.cartObj && simply.cartObj.attributes) simply.cartObj.attributes.ACTIVE_TAB = id;
    // Debounce the server write so rapid swipes don't fire a burst of cart updates.
    clearTimeout(cartTimer);
    cartTimer = setTimeout(() => {
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { ACTIVE_TAB: id } }),
        }).catch(() => { });
      } catch (e) { }
    }, 350);
  };

  /* ---------------- chrome (identity only) ---------------- */
  const drawerTabs = () => Array.prototype.slice.call(document.querySelectorAll('.top-menu-bar li'));


  const drawerVisible = () => {
    const bar = document.querySelector('.sidebar-menubar');
    return !!bar && !bar.classList.contains('hide') && !bar.classList.contains('no-height-sidebar');
  };

  // The default homepage ('') has no layout of its own; fall back to the first tab (HOME) so a tab is
  // ALWAYS active in the drawer/header — never "nothing". Header mirrors the drawer for consistency.
  const defaultChromeId = () => {
    const first = document.querySelector('.top-menu-bar li');
    return first ? norm(first.getAttribute('data-cat-id')) : 'HOME';
  };


  const moveSwiper = (id, refresh) => {
    if (!swiper || !id) return;
    if (!drawerVisible()) return; // never touch a hidden swiper — the next drawer-open re-syncs it
    const idx = drawerTabs().findIndex((el) => norm(el.getAttribute('data-cat-id')) === id);
    if (idx < 0) return;
    const already = swiper.realIndex === idx;
    if (!refresh && already) return;
    suppressSwiper = true;
    try {
      if (refresh && typeof swiper.update === 'function') swiper.update();
      if (!already) {
        if (swiper.params && swiper.params.loop) swiper.slideToLoop(idx, 0, false);
        else swiper.slideTo(idx, 0, false);
      }
    } catch (e) { }
    requestAnimationFrame(() => requestAnimationFrame(() => { suppressSwiper = false; }));
  };

  // Paint the active tab in BOTH menus + the sub-menu panel — NO swiper move (safe anytime).
  const paintTabs = (id) => {
    id = norm(id);
    const chromeId = id || defaultChromeId(); // drawer resting state falls back to HOME
    // Header: only a real (non-default) layout underlines a header tab — the bare "/" default shows NONE.
    document.querySelectorAll('.menu-parent-container .nav-menu-list').forEach((el) => {
      el.classList.toggle('active', !!id && norm(el.getAttribute('data-id')) === id);
    });
    // Drawer (tab + sub-menu panel): always show a resting tab — HOME on the default.
    drawerTabs().forEach((el) => {
      el.classList.toggle('active', norm(el.getAttribute('data-cat-id')) === chromeId);
    });
    document.querySelectorAll('#swiper-menu .sub-menu-ul').forEach((el) => {
      el.classList.toggle('active', norm(el.id) === chromeId);
    });
  };

  // Full chrome sync = paint + MOVE the swiper. Called by set() and registerSwiper (programmatic).
  // Never fights a user gesture: the move is suppressed and is a no-op when already on that slide.
  // Pass refresh=true when the drawer has just become visible (see moveSwiper) so the swiper metrics +
  // active slide are recomputed for the now-laid-out container.
  const renderChrome = (id, refresh) => {
    paintTabs(id);
    moveSwiper(norm(id) || defaultChromeId(), refresh);
  };

  /* ---------------- source of truth ---------------- */
  const current = () => norm(root.getAttribute('data-nb-layout') || '');

  /* ---------------- the reducer ---------------- */
  // Every layout input funnels here. opts: {persist, content, push} — all default true.
  //   content:false  -> chrome + persist only, don't swap #main (used mid-swipe; content is
  //                     debounced separately, and inner pages have no engine to swap).
  //   push:false     -> engine swaps #main without a history push (restore / popstate).
  const set = (id, opts) => {
    opts = opts || {};
    id = norm(id);
    root.setAttribute('data-nb-layout', id);
    renderChrome(id);
    if (opts.persist !== false) persist(id);
    if (opts.content !== false && typeof window.layoutEngineGoTo === 'function') {
      window.layoutEngineGoTo(id, opts.push !== false);
    }
  };

  window.NBLayout = {
    current,
    readPersisted,
    set,
    renderChrome,
    // Back-compat alias: older call sites (and the syncMenuToCategory shim) use syncChrome.
    syncChrome: renderChrome,
    // Store the swiper and paint the resting tab. Normally do NOT move the swiper here: the drawer is
    // hidden (display:none → 0 width), and moving a loop swiper while hidden corrupts its position /
    // active-slide (that showed the wrong sub-menu panel on the very first open until you reopened);
    // the drawer-open handler positions it once the container is visible.
    // EXCEPTION: if the user opened the drawer DURING page load — before the swiper existed — the
    // open handler's move was a no-op (swiper was null) and nothing else will reposition it. So if the
    // drawer is already open when the swiper finally registers, position it now (it IS visible).
    // EOSS keeps the old paint+move here — it drives the swiper by its own separate logic.
    registerSwiper: (sw) => {
      swiper = sw;
      if (window.simply && simply.EOSSEnable) { renderChrome(current()); return; }
      const bar = document.querySelector('.sidebar-menubar');
      const drawerOpen = bar && !bar.classList.contains('hide') && !bar.classList.contains('no-height-sidebar');
      if (drawerOpen) renderChrome(current(), true);
      else paintTabs(current());
    },
    swiperSuppressed: () => suppressSwiper,
  };

  /* ---------------- logo = always the default homepage ---------------- */
  // On the homepage (engine present, non-EOSS) the logo soft-swaps to the default and clears the
  // persisted layout — no reload. On inner pages / EOSS it's a real navigation to "/"; we only clear
  // persistence here and let the browser (and the legacy inner-logo handler) do the rest.
  document.addEventListener('click', function (e) {
    const logo = e.target && e.target.closest && e.target.closest('.Header__LogoLink');
    if (!logo) return;
    if (typeof window.layoutEngineGoTo === 'function' && !(window.simply && simply.EOSSEnable)) {
      e.preventDefault();
      e.stopPropagation();
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'TOP_NAV_CLICK',
        event_category: 'icon_topnav_click',
        event_action: logo.dataset.event_name || 'homepage logo',
        event_label: window.location.pathname,
      });
      set('', { content: true, push: true });
    } else {
      persist('');
    }
  }, true);

  // A homepage sale-hero pill switches to a layout by id (content lives inside #main, so the listener
  // is delegated and survives content swaps). Only act on a REAL engine layout — asked from the engine
  // in-memory (window.layoutEngineHas), never by reading #layout-engine-config, which is inside #main
  // and is destroyed on the first content swap. Unknown id -> let the pill's default happen; never
  // clear the chrome for it.
  document.addEventListener('click', function (e) {
    const pill = e.target && e.target.closest && e.target.closest('.sale-hero__pill');
    if (!pill) return;
    const id = norm(pill.getAttribute('data-id'));
    if (!id) return;
    if (typeof window.layoutEngineHas !== 'function' || !window.layoutEngineHas(id)) return;
    e.preventDefault();
    set(id, { content: true, push: true });
  });

  /* ---------------- initial state (one resolver) ---------------- */
  // Map a ?page_view= value to its layout id using the engine config (the SAME source of truth the
  // engine uses). Present on the homepage in #layout-engine-config (before the engine swaps #main).
  const layoutIdForPageView = (pv) => {
    const el = document.getElementById('layout-engine-config');
    if (!el) return '';
    try {
      const layouts = (JSON.parse(el.textContent).layouts) || [];
      for (let i = 0; i < layouts.length; i++) {
        if ((layouts[i].pageView || '') === pv) return norm(layouts[i].id);
      }
    } catch (e) { }
    return '';
  };

  // Resolve the layout from the FIRST paint so nothing flashes before the engine runs. This is the
  // SAME resolution the engine uses for content, so chrome and content always agree:
  //   ?page_view=<layout>  -> id from the URL (source of truth; the async cart value lags the URL)
  //   ?page_view=home      -> '' (explicit default, no active header tab)
  //   no ?page_view=       -> the nb_layout cookie (inner pages / bare "/")
  // The swiper isn't registered yet here, so we only paint; registerSwiper() positions the swiper.
  (function resolveInitial() {
    let pv = null;
    try { pv = new URLSearchParams(window.location.search).get('page_view'); } catch (e) { }
    let id = '';
    if (pv && pv !== 'home') id = layoutIdForPageView(pv);
    else if (pv === 'home') id = '';
    else id = readPersisted();
    root.setAttribute('data-nb-layout', id);
    paintTabs(id);
  })();
})();
